import { useState, useEffect } from 'react';
import { Plus, Key, Copy, Trash2 } from 'lucide-react';
import { Card, Table, Badge, Button, ConfirmModal, Input } from '@/components/common';
import { oauthService } from '@/services/oauthService';
import { formatDate } from '@/utils/format';
import type { OAuthClient } from '@/types';

export function OAuthClientsPage() {
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    redirect_uris: '',
    grant_types: ['authorization_code'],
    scopes: ['read', 'write'],
    is_confidential: true,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await oauthService.getClients({ per_page: 50 });
      setClients(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load OAuth clients');
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    setLoading(true);
    setError('');
    try {
      await oauthService.createClient({
        ...newClient,
        redirect_uris: newClient.redirect_uris.split('\n').filter(u => u.trim()),
      });
      setCreateModalOpen(false);
      setNewClient({
        name: '',
        redirect_uris: '',
        grant_types: ['authorization_code'],
        scopes: ['read', 'write'],
        is_confidential: true,
      });
      fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    setLoading(true);
    try {
      await oauthService.deleteClient(clientToDelete);
      setDeleteModalOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete client');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.client_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      title: 'Client Name',
      render: (_: any, client: OAuthClient) => (
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-purple-600" />
          <div>
            <p className="font-medium text-secondary-900">{client.name}</p>
            <p className="text-xs text-secondary-500">{client.client_id}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (_: any, client: OAuthClient) => (
        <Badge variant={client.is_confidential ? 'default' : 'warning'}>
          {client.is_confidential ? 'Confidential' : 'Public'}
        </Badge>
      ),
    },
    {
      key: 'grant_types',
      title: 'Grant Types',
      render: (_: any, client: OAuthClient) => (
        <div className="flex flex-wrap gap-1">
          {client.grant_types.map((type, idx) => (
            <Badge key={idx} variant="info" size="sm">
              {type}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'scopes',
      title: 'Scopes',
      render: (_: any, client: OAuthClient) => (
        <div className="flex flex-wrap gap-1">
          {client.scopes.slice(0, 3).map((scope, idx) => (
            <Badge key={idx} variant="info" size="sm">
              {scope}
            </Badge>
          ))}
          {client.scopes.length > 3 && (
            <span className="text-xs text-secondary-500">+{client.scopes.length - 3}</span>
          )}
        </div>
      ),
    },
    {
      key: 'created',
      title: 'Created',
      render: (_: any, client: OAuthClient) => (
        <span className="text-sm text-secondary-600">
          {formatDate(client.created_at, 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '150px',
      render: (_: any, client: OAuthClient) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(client.client_id);
            }}
            className="p-1.5 hover:bg-secondary-100 rounded transition-colors"
            title="Copy Client ID"
          >
            <Copy className="w-4 h-4 text-secondary-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setClientToDelete(client.id);
              setDeleteModalOpen(true);
            }}
            className="p-1.5 hover:bg-red-100 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">OAuth Clients</h1>
          <p className="text-secondary-600 mt-1">Manage OAuth 2.0 client applications</p>
        </div>
        <Button
          icon={<Plus className="w-4 h-4" />}
          onClick={() => setCreateModalOpen(true)}
        >
          New Client
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="flex gap-4">
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search clients..."
          className="max-w-md"
        />
      </div>

      <Card padding="none">
        <Table
          data={filteredClients}
          columns={columns}
          loading={loading}
          emptyMessage="No OAuth clients found"
        />
      </Card>

      {/* Create Client Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Create OAuth Client</h2>
            <div className="space-y-4">
              <Input
                label="Client Name"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="My Application"
                required
              />
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Redirect URIs (one per line)
                </label>
                <textarea
                  value={newClient.redirect_uris}
                  onChange={(e) => setNewClient({ ...newClient, redirect_uris: e.target.value })}
                  placeholder="https://example.com/callback"
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-secondary-300 focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newClient.is_confidential}
                  onChange={(e) => setNewClient({ ...newClient, is_confidential: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label className="text-sm text-secondary-700">
                  Confidential Client (can keep secrets secure)
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleCreateClient} disabled={loading || !newClient.name}>
                Create Client
              </Button>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setClientToDelete(null);
        }}
        onConfirm={handleDeleteClient}
        title="Delete OAuth Client"
        description="Are you sure you want to delete this client? This will revoke all tokens and cannot be undone."
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  );
}
